/**
 * RESPONSIBILITY: draw the /adc page's slim header and its opening hero.
 *
 * The hero is what decides whether somebody reads on, so it carries only what a person
 * deciding whether to come needs: the name of the event, when, where, and the way in. The
 * photograph is a softened backdrop; the words sit on it, never beside it.
 *
 * THE TITLE IS A PICTURE OR WORDS, NEVER BOTH. A lettered logo that says "Austin Desi
 * Christmas" above a heading that says it again is the title twice. With a picture the
 * heading stays in the page for screen readers and search, hidden from sight.
 *
 * The header is deliberately not the site's menu: just the logo, which goes back to the
 * church. It carries no button, because the hero's is a few inches below it — two
 * Register buttons in one screen is one too many.
 */
import { hasImage } from '@framework/core';
import type { AdcTopSection } from '@app/sections';
import { BrandMark } from '../global/BrandMark';
import { Picture } from '../commons/Picture';

function Fact({ label, value }: { label: string; value: string }) {
  return value ? (
    <div className="adc-fact">
      {label ? <span className="adc-fact-label">{label}</span> : null}
      <b>{value}</b>
    </div>
  ) : null;
}

export function AdcTop({ c }: { c: AdcTopSection }) {
  const brand = (
    <>
      <BrandMark logo={hasImage(c.logo) ? c.logo : undefined} name={c.brand} />
      <b>{c.brand}</b>
    </>
  );
  const button = c.registerHref && c.registerLabel
    ? <a className="btn btn-gold" href={c.registerHref}>{c.registerLabel}</a>
    : null;
  const titled = hasImage(c.titleImage);

  return (
    <>
      <header className="adc-nav">
        <div className="adc-nav-bar">
          {c.brandHref ? <a className="brand" href={c.brandHref}>{brand}</a> : <span className="brand">{brand}</span>}
        </div>
      </header>
      {/* Decoration only: a string of lights in the page's own colours. */}
      <div className="adc-lights" aria-hidden="true" />

      <section className="adc-hero">
        {hasImage(c.image) ? <div className="adc-hero-bg" aria-hidden="true"><Picture value={c.image} alt="" /></div> : null}
        <div className="adc-hero-inner">
          {c.tagline ? <p className="adc-eyebrow">{c.tagline}</p> : null}
          {titled ? <Picture value={c.titleImage} alt="" className="adc-wordmark" /> : null}
          {c.heading ? <h1 className={titled ? 'sr-only' : undefined}>{c.heading}</h1> : null}
          {c.when || c.where ? (
            <div className="adc-facts">
              <Fact label={c.whenLabel} value={c.when} />
              <Fact label={c.whereLabel} value={c.where} />
            </div>
          ) : null}
          {button ? <div className="adc-hero-cta">{button}</div> : null}
        </div>
      </section>
    </>
  );
}
